import { useEffect, useState } from "react";
import { getSupabase } from "@/integrations/supabase/lazy";

// Ratings must come from REAL review data only.
// Never fabricate ratings or review counts (audit CI-E).
// Source of truth: the `fetch-reviews` edge function, which reads the store's
// real review metafields, plus user-submitted reviews stored in the DB.

type RatingResult = { rating: number | null; count: number };

const CACHE_PREFIX = "pr-rating:";
const memoryCache = new Map<string, RatingResult>();
const inFlight = new Map<string, Promise<RatingResult>>();

// Limit concurrent edge-function calls so a full grid doesn't stampede.
let active = 0;
const MAX_CONCURRENT = 4;
const queue: Array<() => void> = [];

function runNext() {
  if (active >= MAX_CONCURRENT) return;
  const next = queue.shift();
  if (!next) return;
  active++;
  next();
}

function schedule<T>(task: () => Promise<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    queue.push(() => {
      task()
        .then(resolve, reject)
        .finally(() => {
          active--;
          runNext();
        });
    });
    runNext();
  });
}

function readSessionCache(key: string): RatingResult | null {
  try {
    const raw = sessionStorage.getItem(CACHE_PREFIX + key);
    return raw ? (JSON.parse(raw) as RatingResult) : null;
  } catch {
    return null;
  }
}

function writeSessionCache(key: string, value: RatingResult) {
  try {
    sessionStorage.setItem(CACHE_PREFIX + key, JSON.stringify(value));
  } catch {
    /* storage full / disabled — ignore */
  }
}

async function loadRating(productId: string, handle: string): Promise<RatingResult> {
  const key = handle || productId;

  const cached = memoryCache.get(key) ?? readSessionCache(key);
  if (cached) {
    memoryCache.set(key, cached);
    return cached;
  }

  const existing = inFlight.get(key);
  if (existing) return existing;

  const promise = schedule(async () => {
    let result: RatingResult = { rating: null, count: 0 };
    try {
      const supabase = await getSupabase();
      const { data } = await supabase.functions.invoke("fetch-reviews", {
        body: { productId, handle, page: 1 },
      });
      if (data && typeof data.rating === "number" && data.rating > 0) {
        result = { rating: data.rating, count: data.count || 0 };
      }
    } catch {
      /* network failure — fall back to no rating, never invent one */
    }

    memoryCache.set(key, result);
    writeSessionCache(key, result);
    return result;
  }).finally(() => {
    inFlight.delete(key);
  });

  inFlight.set(key, promise);
  return promise;
}

/**
 * `fetch: false` reads only what this session already loaded (product pages
 * fetch). Grids pass it: one edge call plus a CORS preflight per card was 80
 * requests on a category page, every one of them empty.
 */
export function useProductRating(productId: string, handle: string, options: { fetch?: boolean } = {}): RatingResult {
  const key = handle || productId;
  const fetchMissing = options.fetch ?? true;
  const [result, setResult] = useState<RatingResult>(
    () => memoryCache.get(key) ?? (fetchMissing ? null : readSessionCache(key)) ?? { rating: null, count: 0 }
  );

  useEffect(() => {
    if (!productId && !handle) return;
    if (!fetchMissing) return;
    let cancelled = false;
    loadRating(productId, handle).then((r) => {
      if (!cancelled) setResult(r);
    });
    return () => {
      cancelled = true;
    };
  }, [productId, handle, fetchMissing]);

  return result;
}
